import React, { useState } from 'react';
import { 
  Globe, Cloud, Server, Database, Link, ExternalLink, 
  Code, FileText, Github, Shield, Key, Activity,
  Copy, Check, Search, Filter
} from 'lucide-react';

const linksData = {
  production: {
    title: "🚀 Production Links",
    categories: [
      {
        name: "Cloudflare Pages (Live Sites)",
        icon: Globe,
        links: [
          { name: "Main Web App", url: "https://biig-web.pages.dev", description: "Main application portal" },
          { name: "Projects Directory", url: "https://biig-web.pages.dev/projects", description: "All projects in one place" },
          { name: "Problem Explorer", url: "https://biig-web.pages.dev/problems", description: "36 questions journey" },
          { name: "Question Links", url: "https://biig-web.pages.dev/questions", description: "Individual question experiences" },
          { name: "Single Question (Example)", url: "https://biig-web.pages.dev/problem/love_the_problem", description: "Example single question with music" },
          { name: "Location Pulse", url: "https://biig-web.pages.dev/share", description: "Share your location & activity" },
          { name: "Global Activity Map", url: "https://biig-web.pages.dev/global", description: "Real-time global activities" },
          { name: "Moderation Dashboard", url: "https://biig-web.pages.dev/moderate", description: "Review flagged activities" },
          { name: "Flywheel Dashboard", url: "https://biig-web.pages.dev/flywheel", description: "Business flywheel tracking" },
          { name: "Work Buttons", url: "https://biig-web.pages.dev/work", description: "Workplace automation" },
          { name: "CTO Dashboard", url: "https://biig-web.pages.dev/cto", description: "Tech operations dashboard" },
          { name: "CTO Tech Audit", url: "https://biig-web.pages.dev/cto/audit", description: "Technology audit checklist" },
          { name: "CTO Roadmap", url: "https://biig-web.pages.dev/cto/roadmap", description: "Implementation phases" },
          { name: "CTO Guest Experience", url: "https://biig-web.pages.dev/cto/guest", description: "Guest tech metrics" },
          { name: "CTO Operations", url: "https://biig-web.pages.dev/cto/operations", description: "Operational systems" },
          { name: "Color Palettes", url: "https://biig-web.pages.dev/colors", description: "Premium brand color schemes" },
          { name: "Research Links", url: "https://biig-web.pages.dev/research", description: "AI-powered link insights" },
        ]
      },
      {
        name: "Cloudflare Workers (APIs)",
        icon: Server,
        links: [
          { name: "Main API", url: "https://biig-api.b-9f2.workers.dev", description: "Primary API endpoint" },
          { name: "Health Check", url: "https://biig-api.b-9f2.workers.dev/api/health", description: "API status check" },
          { name: "Problem Explorer API", url: "https://biig-api.b-9f2.workers.dev/api/problem-explorer", description: "Session & answer storage" },
          { name: "Flywheel API", url: "https://biig-api.b-9f2.workers.dev/api/flywheel", description: "Flywheel endpoints" },
          { name: "WhatsApp API", url: "https://biig-api.b-9f2.workers.dev/api/whatsapp", description: "WhatsApp messaging" },
          { name: "Telegram API", url: "https://biig-api.b-9f2.workers.dev/api/telegram", description: "Telegram bot integration" },
          { name: "Location API", url: "https://biig-api.b-9f2.workers.dev/api/location", description: "Location services" },
          { name: "Global Activities API", url: "https://biig-api.b-9f2.workers.dev/api/global", description: "Global activity tracking" },
          { name: "Dev Tools API", url: "https://biig-api.b-9f2.workers.dev/api/dev", description: "Development utilities" },
          { name: "CTO Audit API", url: "https://biig-api.b-9f2.workers.dev/api/cto/audit", description: "Tech audit endpoints" },
          { name: "CTO Phases API", url: "https://biig-api.b-9f2.workers.dev/api/cto/phases", description: "Implementation phases" },
          { name: "CTO Systems API", url: "https://biig-api.b-9f2.workers.dev/api/cto/systems", description: "System status monitoring" },
        ]
      },
      {
        name: "Recent Deployments",
        icon: Activity,
        links: [
          { name: "Latest Web Deploy", url: "https://0d1187b4.biig-web.pages.dev", description: "Most recent deployment" },
          { name: "Previous Deploy", url: "https://7a56b679.biig-web.pages.dev", description: "Previous stable version" },
          { name: "Music Update", url: "https://ec8ad286.biig-web.pages.dev", description: "With Infinitivo music" },
          { name: "Bilingual Update", url: "https://6b3f1286.biig-web.pages.dev", description: "PT-BR translation added" },
        ]
      }
    ]
  },
  development: {
    title: "🛠️ Development Links",
    categories: [
      {
        name: "Local Development",
        icon: Code,
        links: [
          { name: "Web App (Local)", url: "http://localhost:3001", description: "Local web development" },
          { name: "API (Local)", url: "http://localhost:8787", description: "Local API server" },
          { name: "Files Service (Local)", url: "http://localhost:8788", description: "Local file storage" },
          { name: "Email Service (Local)", url: "http://localhost:8789", description: "Local email service" },
          { name: "Docs Site (Local)", url: "http://localhost:3002", description: "Documentation site" },
          { name: "Work Buttons (Local)", url: "http://localhost:5174", description: "Work buttons app" },
          { name: "CTO Dashboard (Local)", url: "http://localhost:5173/cto", description: "CTO dashboard" },
          { name: "CTO Tech Audit (Local)", url: "http://localhost:5173/cto/audit", description: "Tech audit" },
          { name: "CTO Roadmap (Local)", url: "http://localhost:5173/cto/roadmap", description: "Implementation roadmap" },
          { name: "Color Palettes (Local)", url: "http://localhost:5173/colors", description: "Premium color schemes" },
          { name: "Research Links (Local)", url: "http://localhost:5173/research", description: "AI-powered link insights" },
        ]
      },
      {
        name: "Webhooks & Integrations",
        icon: Link,
        links: [
          { name: "Instagram Webhook", url: "https://biig-api.b-9f2.workers.dev/api/flywheel/webhooks/instagram", description: "Instagram message handler" },
          { name: "WhatsApp Webhook", url: "https://biig-api.b-9f2.workers.dev/api/flywheel/webhooks/whatsapp", description: "WhatsApp message handler" },
          { name: "Stripe Webhook", url: "https://biig-api.b-9f2.workers.dev/api/flywheel/webhooks/stripe", description: "Payment processing" },
          { name: "Telegram Webhook", url: "https://biig-api.b-9f2.workers.dev/api/telegram/webhook", description: "Telegram bot webhook" },
        ]
      }
    ]
  },
  infrastructure: {
    title: "🏗️ Infrastructure",
    categories: [
      {
        name: "Cloudflare Resources",
        icon: Cloud,
        links: [
          { name: "D1 Database", url: "biig-biz-db", description: "SQLite database (ID: f8b7063f-c940-47e4-82b9-afa7fb75f14f)" },
          { name: "KV Namespace", url: "CACHE", description: "Key-value storage (ID: e4ed006f200f4298b2a42c6b35e8a081)" },
          { name: "R2 Bucket", url: "cf-storage", description: "Object storage bucket" },
          { name: "Workers AI", url: "AI Binding", description: "AI model access" },
        ]
      },
      {
        name: "Environment Variables",
        icon: Key,
        links: [
          { name: "TELEGRAM_BOT_TOKEN", url: "Secret", description: "Telegram bot authentication" },
          { name: "WHATSAPP_TOKEN", url: "Secret", description: "WhatsApp API token" },
          { name: "WHATSAPP_PHONE_ID", url: "Secret", description: "WhatsApp phone number ID" },
          { name: "OPENAI_API_KEY", url: "Secret", description: "OpenAI API access" },
          { name: "ANTHROPIC_API_KEY", url: "Secret", description: "Anthropic Claude API" },
        ]
      }
    ]
  },
  documentation: {
    title: "📚 Documentation",
    categories: [
      {
        name: "External Services",
        icon: ExternalLink,
        links: [
          { name: "Cloudflare Dashboard", url: "https://dash.cloudflare.com", description: "Manage workers & pages" },
          { name: "GitHub Repository", url: "https://github.com/biig-tech/biig-biz", description: "Source code (if public)" },
          { name: "WhatsApp Business", url: "https://business.facebook.com", description: "WhatsApp configuration" },
          { name: "Telegram BotFather", url: "https://t.me/botfather", description: "Telegram bot management" },
          { name: "OpenAI Platform", url: "https://platform.openai.com", description: "OpenAI API management" },
          { name: "Anthropic Console", url: "https://console.anthropic.com", description: "Claude API management" },
        ]
      },
      {
        name: "Test Endpoints",
        icon: Shield,
        links: [
          { name: "Full Loop Test", url: "https://biig-api.b-9f2.workers.dev/api/flywheel/test/full-loop", description: "Test complete flow" },
          { name: "Instagram DM Test", url: "https://biig-api.b-9f2.workers.dev/api/flywheel/test/instagram-dm", description: "Test Instagram messaging" },
          { name: "WhatsApp Test", url: "https://biig-api.b-9f2.workers.dev/api/flywheel/test/whatsapp-message", description: "Test WhatsApp messaging" },
          { name: "Payment Test", url: "https://biig-api.b-9f2.workers.dev/api/flywheel/test/payment", description: "Test payment flow" },
          { name: "Telegram Test", url: "https://biig-api.b-9f2.workers.dev/api/telegram/test", description: "Test Telegram bot" },
        ]
      }
    ]
  }
};

export default function BiigLinks() {
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const allCategories = Object.values(linksData).flatMap(section => 
    section.categories.map(cat => cat.name)
  );

  const filteredLinks = Object.entries(linksData).map(([key, section]) => ({
    ...section,
    categories: section.categories
      .filter(category => selectedCategory === 'all' || category.name === selectedCategory)
      .map(category => ({
        ...category,
        links: category.links.filter(link => 
          searchTerm === '' || 
          link.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          link.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
          link.description.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }))
      .filter(category => category.links.length > 0)
  })).filter(section => section.categories.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Biig Links Reference
          </h1>
          <p className="text-gray-400 text-lg">
            All workers, pages, APIs, and endpoints in one place
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-8 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search links, URLs, or descriptions..."
              className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-3 text-gray-400" size={20} />
            <select
              className="pl-10 pr-8 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none appearance-none cursor-pointer"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {[...new Set(allCategories)].map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Links Sections */}
        {filteredLinks.map((section, sectionIdx) => (
          <div key={sectionIdx} className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-gray-100">
              {section.title}
            </h2>
            
            {section.categories.map((category, catIdx) => {
              const IconComponent = category.icon;
              return (
                <div key={catIdx} className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <IconComponent className="text-blue-400" size={24} />
                    <h3 className="text-xl font-semibold text-gray-200">
                      {category.name}
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.links.map((link, linkIdx) => (
                      <div
                        key={linkIdx}
                        className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-blue-500 transition-all group"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium text-white group-hover:text-blue-400 transition-colors">
                            {link.name}
                          </h4>
                          <button
                            onClick={() => copyToClipboard(link.url)}
                            className="text-gray-400 hover:text-blue-400 transition-colors"
                            title="Copy URL"
                          >
                            {copiedUrl === link.url ? (
                              <Check size={16} className="text-green-400" />
                            ) : (
                              <Copy size={16} />
                            )}
                          </button>
                        </div>
                        
                        <p className="text-sm text-gray-400 mb-2">
                          {link.description}
                        </p>
                        
                        <div className="flex items-center gap-2">
                          {link.url.startsWith('http') ? (
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:text-blue-300 break-all flex items-center gap-1"
                            >
                              {link.url}
                              <ExternalLink size={12} />
                            </a>
                          ) : (
                            <span className="text-xs text-gray-500 font-mono">
                              {link.url}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {/* Stats */}
        <div className="mt-12 pt-8 border-t border-gray-700 text-center">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-400">10+</div>
              <div className="text-sm text-gray-400">Pages Sites</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-400">9+</div>
              <div className="text-sm text-gray-400">Worker APIs</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-400">4+</div>
              <div className="text-sm text-gray-400">Databases</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-2xl font-bold text-orange-400">50+</div>
              <div className="text-sm text-gray-400">Total Endpoints</div>
            </div>
          </div>
          
          <p className="text-gray-500 text-sm">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
}